/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useSpeedDialSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling Speed Dial form submission and deletion.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createSpeedDial, updateSpeedDial, deleteSpeedDial } from '../api/speedDialsApi';
import type { SpeedDialFormState } from './useSpeedDialData';
import { DEFAULT_SPEED_DIAL_FORM } from './useSpeedDialData';

export function useSpeedDialSubmit(
  formData: SpeedDialFormState,
  baseline: SpeedDialFormState | null,
  hasChanges: boolean,
  validateForm: () => { isValid: boolean, errors: Record<string, string> },
  mode: 'create' | 'edit',
  id: string | undefined,
  setFormData: (data: SpeedDialFormState | ((prev: SpeedDialFormState) => SpeedDialFormState)) => void,
  setBaseline: (data: SpeedDialFormState | null) => void
) {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const translateRef = useRef(t);
  const showSuccessRef = useRef(showSuccess);
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  useEffect(() => {
    translateRef.current = t;
    showSuccessRef.current = showSuccess;
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [t, showSuccess, showError, showInfo]);

  const handleFormSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault();

      const translate = translateRef.current;

      const validation = validateForm();
      if (!validation.isValid) {
        const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
        showErrorRef.current?.(`${translate('speedDials.messages.validationError', 'Please fix the errors in the form')}${errorMsgs ? ': ' + errorMsgs : ''}`);
        return undefined;
      }

      setIsSaving(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = {
          speedCode: formData.speedCode.trim(),
          destination: formData.destination.trim(),
          description: formData.description.trim() || null,
          classOfServicesId: formData.classOfServicesId || null,
          enabled: formData.enabled
        };

        let result;
        if (mode === 'edit' && id && id !== 'new') {
          result = await updateSpeedDial(id, payload);
        } else {
          result = await createSpeedDial(payload);
        }

        showSuccessRef.current?.(
          mode === 'edit'
            ? translate('speedDials.messages.saveSuccess')
            : translate('speedDials.messages.saveSuccess')
        );

        // Update baseline after successful save
        if (result.data) {
          const newFormState: SpeedDialFormState = {
            speedCode: result.data.speedCode || '',
            destination: result.data.destination || '',
            description: result.data.description || '',
            classOfServicesId: result.data.classOfServicesId ?? null,
            enabled: result.data.enabled ?? true
          };
          setFormData(newFormState);
          setBaseline(newFormState);
        }

        // Return saved data (navigation is now handled by useSaveWithAction)
        return result.data || { ...payload, id: result.data?.id };
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : translate('speedDials.messages.saveError');
        showErrorRef.current?.(message);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [formData, hasChanges, validateForm, mode, id, setFormData, setBaseline]
  );

  const handleCancel = useCallback(() => {
    if (mode === 'edit' && baseline) {
      // Restore original form data
      setFormData({ ...baseline });
    } else {
      // Reset to defaults in create mode
      setFormData({ ...DEFAULT_SPEED_DIAL_FORM });
    }
  }, [mode, baseline, setFormData]);

  const handleDelete = useCallback(async () => {
    if (!id || id === 'new') return;

    const translate = translateRef.current;
    setIsDeleting(true);
    try {
      await deleteSpeedDial(id);
      showSuccessRef.current?.(translate('speedDials.messages.deleteSuccess'));
      setShowDeleteModal(false);
      navigate('/pbx/applications/speed-dialing');
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : translate('speedDials.messages.deleteError');
      showErrorRef.current?.(message);
    } finally {
      setIsDeleting(false);
    }
  }, [id, navigate]);

  return {
    isSaving,
    isDeleting,
    showDeleteModal,
    setShowDeleteModal,
    handleFormSubmit,
    handleCancel,
    handleDelete
  };
}

